// Converts between the schedule generator API config and the editable form state.

export const MAX_WEEKS = 60;

export const DEFAULT_RULES = {
  gamesPerTeamPerWeek: 1,
  gameLengthMinutes: 60,
  maxConsecutiveSameTime: 2,
  flipHomeAwayOnRepeat: true,
};

export const createEmptySlot = (overrides = {}) => ({
  dayOfWeek: 'Thursday',
  startTime: '20:30',
  venueId: '',
  fieldId: '',
  ...overrides,
});

export const createEmptyBlackout = () => ({ startDate: '', endDate: '', label: '' });

export const GAME_TYPE_OPTIONS = [
  { value: 'Regular', label: 'Regular season' },
  { value: 'Playoff', label: 'Playoff' },
  { value: 'SemiFinal', label: 'Semi-final' },
  { value: 'Final', label: 'Final' },
  { value: 'Friendly', label: 'Friendly' },
  { value: 'Bye', label: 'Bye' },
];

export const getGameTypeLabel = (gameType) =>
  GAME_TYPE_OPTIONS.find((o) => o.value === gameType)?.label || gameType || '';

export const MAX_QUICK_ADD_GAMES = 32;

export const createEmptyExtraGame = (overrides = {}) => ({
  date: '',
  startTime: '20:30',
  gameType: 'Playoff',
  venueId: '',
  fieldId: '',
  homeTeamId: '',
  awayTeamId: '',
  homeLabel: '',
  awayLabel: '',
  ...overrides,
});

const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeTime = (value) => (value ? String(value).slice(0, 5) : '');

// Forms and configs saved before playoffs/other games were added always included the regular season.
export const isRegularSeasonIncluded = (formOrConfig) => formOrConfig?.includeRegularSeason !== false;

const trimOrNull = (value) => (value || '').trim() || null;

const MINUTES_PER_DAY = 1440;

const pad2 = (value) => String(value).padStart(2, '0');

// Adds minutes to a 'yyyy-MM-dd' date + 'HH:mm' time, rolling the date over midnight.
export const addMinutesToDateTime = (date, time, minutes) => {
  const [h, m] = normalizeTime(time).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return { date, startTime: '' };
  const total = h * 60 + m + minutes;
  const dayOffset = Math.floor(total / MINUTES_PER_DAY);
  const minuteOfDay = ((total % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const startTime = `${pad2(Math.floor(minuteOfDay / 60))}:${pad2(minuteOfDay % 60)}`;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!match || dayOffset === 0) return { date, startTime };
  const shifted = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + dayOffset);
  return {
    date: `${shifted.getFullYear()}-${pad2(shifted.getMonth() + 1)}-${pad2(shifted.getDate())}`,
    startTime,
  };
};

export const addMinutesToTime = (time, minutes) => addMinutesToDateTime('', time, minutes).startTime;

export const createNewForm = ({ name = '', teamIds = [], slots } = {}) => ({
  name,
  includeRegularSeason: true,
  teamIds: [...teamIds],
  startDate: '',
  numberOfWeeks: '',
  endDate: '',
  blackouts: [],
  slots: slots && slots.length ? slots : [createEmptySlot()],
  gamesPerTeamPerWeek: String(DEFAULT_RULES.gamesPerTeamPerWeek),
  gameLengthMinutes: String(DEFAULT_RULES.gameLengthMinutes),
  maxConsecutiveSameTime: String(DEFAULT_RULES.maxConsecutiveSameTime),
  flipHomeAwayOnRepeat: DEFAULT_RULES.flipHomeAwayOnRepeat,
  extraGames: [],
});

export const draftToForm = (draft) => {
  const config = draft?.config || {};
  return {
    name: draft?.name || '',
    includeRegularSeason: config.includeRegularSeason ?? true,
    teamIds: [...(config.teamIds || [])],
    startDate: config.startDate || '',
    numberOfWeeks: toInputValue(config.numberOfWeeks),
    endDate: config.endDate || '',
    blackouts: (config.blackouts || []).map((b) => ({
      startDate: b.startDate || '',
      endDate: b.endDate || '',
      label: b.label || '',
    })),
    slots: config.slots?.length
      ? config.slots.map((s) => ({
        dayOfWeek: s.dayOfWeek || 'Thursday',
        startTime: normalizeTime(s.startTime),
        venueId: toInputValue(s.venueId),
        fieldId: toInputValue(s.fieldId),
      }))
      : [createEmptySlot()],
    gamesPerTeamPerWeek: toInputValue(config.gamesPerTeamPerWeek ?? DEFAULT_RULES.gamesPerTeamPerWeek),
    gameLengthMinutes: toInputValue(config.gameLengthMinutes ?? DEFAULT_RULES.gameLengthMinutes),
    maxConsecutiveSameTime: toInputValue(config.maxConsecutiveSameTime ?? DEFAULT_RULES.maxConsecutiveSameTime),
    flipHomeAwayOnRepeat: config.flipHomeAwayOnRepeat ?? DEFAULT_RULES.flipHomeAwayOnRepeat,
    extraGames: (config.extraGames || []).map((g) => ({
      date: g.date || '',
      startTime: normalizeTime(g.startTime),
      gameType: g.gameType || 'Playoff',
      venueId: toInputValue(g.venueId),
      fieldId: toInputValue(g.fieldId),
      homeTeamId: toInputValue(g.homeTeamId),
      awayTeamId: toInputValue(g.awayTeamId),
      homeLabel: g.homeLabel || '',
      awayLabel: g.awayLabel || '',
    })),
  };
};

export const slotsToPayload = (slots) =>
  slots.map((s) => ({
    dayOfWeek: s.dayOfWeek,
    startTime: normalizeTime(s.startTime),
    venueId: toNumberOrNull(s.venueId),
    fieldId: toNumberOrNull(s.fieldId),
  }));

export const templateToSlots = (template) =>
  (template?.slots || []).map((s) => ({
    dayOfWeek: s.dayOfWeek || 'Thursday',
    startTime: normalizeTime(s.startTime),
    venueId: toInputValue(s.venueId),
    fieldId: toInputValue(s.fieldId),
  }));

export const extraGamesToPayload = (games = []) =>
  games.map((g) => {
    const isBye = g.gameType === 'Bye';
    const homeTeamId = toNumberOrNull(g.homeTeamId);
    const awayTeamId = isBye ? null : toNumberOrNull(g.awayTeamId);
    return {
      date: g.date || null,
      startTime: normalizeTime(g.startTime),
      gameType: g.gameType,
      venueId: isBye ? null : toNumberOrNull(g.venueId),
      fieldId: isBye ? null : toNumberOrNull(g.fieldId),
      homeTeamId,
      awayTeamId,
      homeLabel: isBye || homeTeamId !== null ? null : trimOrNull(g.homeLabel),
      awayLabel: isBye || awayTeamId !== null ? null : trimOrNull(g.awayLabel),
    };
  });

// Games fill every selected field at one kick-off time before moving to the next kick-off.
const placeGames = ({ count, date, fieldIds = [], firstTime, gapMinutes }) => {
  const fields = fieldIds.length ? fieldIds.map(String) : [''];
  return Array.from({ length: count }, (_, i) => ({
    fieldId: fields[i % fields.length],
    ...addMinutesToDateTime(date, firstTime, Math.floor(i / fields.length) * gapMinutes),
  }));
};

export const buildPlayoffRoundGames = ({
  gameType = 'Playoff',
  date,
  venueId,
  fieldIds = [],
  firstTime,
  count,
  gapMinutes,
}) =>
  placeGames({ count, date, fieldIds, firstTime, gapMinutes }).map((slot) =>
    createEmptyExtraGame({ ...slot, gameType, venueId: toInputValue(venueId) }));

export const buildSemiFinalAndFinalGames = ({
  semiDate,
  semiTime,
  finalDate,
  finalTime,
  venueId,
  fieldIds = [],
  gapMinutes,
}) => {
  const pairings = [['1st', '4th'], ['2nd', '3rd']];
  const semis = placeGames({ count: 2, date: semiDate, fieldIds, firstTime: semiTime, gapMinutes }).map((slot, i) =>
    createEmptyExtraGame({
      ...slot,
      gameType: 'SemiFinal',
      venueId: toInputValue(venueId),
      homeLabel: pairings[i][0],
      awayLabel: pairings[i][1],
    }));
  const final = createEmptyExtraGame({
    date: finalDate,
    startTime: normalizeTime(finalTime),
    gameType: 'Final',
    venueId: toInputValue(venueId),
    fieldId: fieldIds.length ? String(fieldIds[0]) : '',
    homeLabel: 'Winner SF1',
    awayLabel: 'Winner SF2',
  });
  return [...semis, final];
};

// Without a regular season the hidden round-robin settings are kept, minus incomplete
// slots and blackouts, which the API can't parse and ignores anyway.
const isWholeNumberInRange = (value, min, max) =>
  value !== null && Number.isInteger(value) && value >= min && value <= max;

// Hidden round-robin numbers aren't validated, so invalid ones (e.g. 1.5) fall back instead of
// failing the API's integer binding.
const hiddenWholeNumber = (value, min, max, fallback) => {
  const number = toNumberOrNull(value);
  return isWholeNumberInRange(number, min, max) ? number : fallback;
};

const keptSlots = (form, includeRegularSeason) => (includeRegularSeason
  ? form.slots
  : form.slots.filter((s) => s.dayOfWeek && s.startTime && s.venueId));

const keptBlackouts = (form, includeRegularSeason) => (includeRegularSeason
  ? form.blackouts
  : form.blackouts.filter((b) => b.startDate));

export const formToPayload = (form) => {
  const includeRegularSeason = isRegularSeasonIncluded(form);
  return {
    name: (form.name || '').trim(),
    config: {
      includeRegularSeason,
      teamIds: [...form.teamIds].map(Number).sort((a, b) => a - b),
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      numberOfWeeks: includeRegularSeason
        ? toNumberOrNull(form.numberOfWeeks)
        : hiddenWholeNumber(form.numberOfWeeks, 1, MAX_WEEKS, null),
      blackouts: keptBlackouts(form, includeRegularSeason).map((b) => ({
        startDate: b.startDate || null,
        endDate: b.endDate || null,
        label: (b.label || '').trim() || null,
      })),
      slots: slotsToPayload(keptSlots(form, includeRegularSeason)),
      gamesPerTeamPerWeek: includeRegularSeason
        ? toNumberOrNull(form.gamesPerTeamPerWeek) ?? DEFAULT_RULES.gamesPerTeamPerWeek
        : hiddenWholeNumber(form.gamesPerTeamPerWeek, 1, 3, DEFAULT_RULES.gamesPerTeamPerWeek),
      gameLengthMinutes: toNumberOrNull(form.gameLengthMinutes) ?? DEFAULT_RULES.gameLengthMinutes,
      maxConsecutiveSameTime: includeRegularSeason
        ? toNumberOrNull(form.maxConsecutiveSameTime) ?? DEFAULT_RULES.maxConsecutiveSameTime
        : hiddenWholeNumber(form.maxConsecutiveSameTime, 1, MAX_WEEKS, DEFAULT_RULES.maxConsecutiveSameTime),
      flipHomeAwayOnRepeat: Boolean(form.flipHomeAwayOnRepeat),
      extraGames: extraGamesToPayload(form.extraGames || []),
    },
  };
};

const validateRegularSeason = (form) => {
  if (form.teamIds.length < 2) return 'Select at least two teams.';
  if (!form.startDate) return 'Choose a start date.';
  const weeks = toNumberOrNull(form.numberOfWeeks);
  if (!form.endDate && weeks === null) return 'Enter the number of game weeks, an end date, or both.';
  if (weeks !== null && !isWholeNumberInRange(weeks, 1, MAX_WEEKS)) return `Number of game weeks must be between 1 and ${MAX_WEEKS}.`;
  if (form.endDate && form.endDate < form.startDate) return 'End date must be on or after the start date.';
  if (form.blackouts.some((b) => !b.startDate)) return 'Each blackout needs a start date.';
  if (form.blackouts.some((b) => b.endDate && b.endDate < b.startDate)) return 'A blackout ends before it starts.';
  if (form.slots.length === 0) return 'Add at least one weekly game slot.';
  if (form.slots.some((s) => !s.dayOfWeek || !s.startTime || !s.venueId)) return 'Each weekly slot needs a day, time and venue.';
  if (!isWholeNumberInRange(toNumberOrNull(form.gamesPerTeamPerWeek), 1, 3)) return 'Games per team per week must be between 1 and 3.';
  if (!isWholeNumberInRange(toNumberOrNull(form.maxConsecutiveSameTime), 1, MAX_WEEKS)) return 'Max consecutive weeks in the same time slot must be at least 1.';
  return null;
};

const validateExtraGame = (game) => {
  if (!game.date || !game.startTime) return 'Each playoff/other game needs a date and start time.';
  if (game.gameType === 'Bye') return game.homeTeamId ? null : 'A BYE needs a home team.';
  if (!game.venueId) return 'Each playoff/other game needs a venue.';
  if (Boolean(game.homeTeamId) !== Boolean(game.awayTeamId)) return 'Set both teams, or leave both as TBD.';
  if (game.homeTeamId && String(game.homeTeamId) === String(game.awayTeamId)) return "A team can't play itself.";
  return null;
};

// Returns a user-facing error message, or null when the form can be submitted.
// The API re-validates everything; these checks just catch obvious gaps early.
export const validateForm = (form) => {
  if (!(form.name || '').trim()) return 'Enter a name for this schedule.';
  if (isRegularSeasonIncluded(form)) {
    const regularSeasonError = validateRegularSeason(form);
    if (regularSeasonError) return regularSeasonError;
  }
  if (!isWholeNumberInRange(toNumberOrNull(form.gameLengthMinutes), 15, 300)) return 'Game length must be between 15 and 300 minutes.';
  const extraGames = form.extraGames || [];
  if (!isRegularSeasonIncluded(form) && extraGames.length === 0) return 'Add at least one game, or include the regular season.';
  for (const game of extraGames) {
    const error = validateExtraGame(game);
    if (error) return error;
  }
  return null;
};

export const isFormUnchanged = (form, draft) => {
  if (!form || !draft) return false;
  return JSON.stringify(formToPayload(form)) === JSON.stringify(formToPayload(draftToForm(draft)));
};
